import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import './CanineHealthTesting.css';

const CanineHealthTesting = ({ playerId }) => {
  const [dogs, setDogs] = useState([]);
  const [selectedDogId, setSelectedDogId] = useState('');
  const [testType, setTestType] = useState('');
  const [customTests, setCustomTests] = useState([{ disease: '', result: '', writeInType: 'bbp' }]);
  const [externalLink, setExternalLink] = useState('');
  const [bbpDiseases, setBbpDiseases] = useState([]);
  const [availableBbpDiseases, setAvailableBbpDiseases] = useState([]);
  const [availableBodyTests, setAvailableBodyTests] = useState(['Hips', 'Elbows', 'Patellas', 'Thyroid', 'Eyes', 'Hearing', 'Cardiac']);
  const [canBuyTests, setCanBuyTests] = useState({});
  const [selectedDogTests, setSelectedDogTests] = useState([]);
  const [selectedDogArtUrl, setSelectedDogArtUrl] = useState('');
  const [pendingTests, setPendingTests] = useState([]);
  const [testedDiseases, setTestedDiseases] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dogsRes, bbpRes] = await Promise.all([
          fetch(`${API_BASE_URL}/health-testing-dogs?playerId=${playerId}`)
            .then(async res => {
              const data = await res.json();
              console.log('Raw dogs response:', data);
              return data;
            }),
          fetch(`${API_BASE_URL}/bbp-diseases`).then(res => res.json())
        ]);

        const dogsData = Array.isArray(dogsRes) ? dogsRes : [];
        if (!Array.isArray(dogsRes)) {
          console.error('Dogs response is not an array:', dogsRes);
        }

        console.log('Processed dogs data:', dogsData);
        console.log('BBP diseases response:', bbpRes);

        setDogs(dogsData);
        setBbpDiseases(bbpRes);
        setAvailableBbpDiseases(bbpRes);

        const initialCanBuyTests = dogsData.reduce((acc, dog) => {
          acc[dog.dogId] = { canBuyBBP: true, canBuyBody: true, canBuyWriteIn: true };
          return acc;
        }, {});
        setCanBuyTests(initialCanBuyTests);
      } catch (err) {
        console.error('Error fetching data:', err);
        setDogs([]);
      }
    };
    fetchData();
  }, [playerId]);

  useEffect(() => {
    if (selectedDogId) {
      const selectedDog = dogs.find(dog => dog.dogId === selectedDogId);
      if (selectedDog) {
        console.log('Selected dog data:', {
          dogId: selectedDog.dogId,
          registeredName: selectedDog.registeredName,
          artUrl: selectedDog.artUrl,
          stashLink: selectedDog.stashLink
        });
        setSelectedDogArtUrl(selectedDog.artUrl || '');

        const testedDiseasesList = [
          ...(selectedDog.healthTestRecords || []).map(r => (r.testName || r.disease).replace('BBP - ', '')),
          ...(selectedDog.customHealthTests || []).filter(t => t.testType !== 'write-in').map(t => t.disease)
        ];
        setTestedDiseases(testedDiseasesList);

        const testedBodyTests = testedDiseasesList.filter(t => t.startsWith('Body Scores - ')).map(t => t.replace('Body Scores - ', ''));
        const untestedBody = ['Hips', 'Elbows', 'Patellas', 'Thyroid', 'Eyes', 'Hearing', 'Cardiac']
          .filter(t => !testedBodyTests.includes(t));

        const parseBreeds = (breedString) => {
          return breedString.split(' x ').map(b => {
            const match = b.match(/(.+?)(?:\s*\(\d*\.?\d+%\))?$/);
            const cleanBreed = match ? match[1].trim() : b.trim();
            return cleanBreed.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
          });
        };
        const breeds = parseBreeds(selectedDog.breed);

        fetch(`${API_BASE_URL}/bbp-diseases-for-breeds`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ breeds })
        })
          .then(async res => {
            if (!res.ok) {
              const text = await res.text();
              throw new Error(`Server responded with status ${res.status}: ${text}`);
            }
            return res.json();
          })
          .then(breedBbpDiseases => {
            // Check if all breed-specific BBP diseases are tested
            const untestedBbp = breedBbpDiseases.filter(d => !testedDiseasesList.includes(d));
            console.log('BBP eligibility:', {
              breedBbpDiseases: breedBbpDiseases.length,
              testedDiseases: testedDiseasesList.length,
              untestedBbp: untestedBbp.length
            });

            setAvailableBbpDiseases(breedBbpDiseases);
            setAvailableBodyTests(untestedBody);
            setCanBuyTests(prev => ({
              ...prev,
              [selectedDogId]: {
                canBuyBBP: untestedBbp.length > 0,
                canBuyBody: untestedBody.length > 0,
                canBuyWriteIn: true
              }
            }));
            setSelectedDogTests([
              ...(selectedDog.healthTestRecords || []),
              ...(selectedDog.customHealthTests || [])
            ]);
          })
          .catch(err => {
            console.error('Error fetching breed BBP diseases:', err);
            setAvailableBbpDiseases([]);
            setAvailableBodyTests([]);
            setCanBuyTests(prev => ({
              ...prev,
              [selectedDogId]: { canBuyBBP: false, canBuyBody: false, canBuyWriteIn: true }
            }));
          });
      }
    } else {
      setSelectedDogArtUrl('');
      setTestedDiseases([]);
      setAvailableBbpDiseases(bbpDiseases);
      setAvailableBodyTests(['Hips', 'Elbows', 'Patellas', 'Thyroid', 'Eyes', 'Hearing', 'Cardiac']);
      setSelectedDogTests([]);
      setPendingTests([]);
      setCanBuyTests(prev => ({
        ...prev,
        [selectedDogId]: { canBuyBBP: true, canBuyBody: true, canBuyWriteIn: true }
      }));
    }
  }, [selectedDogId, dogs, bbpDiseases]);

  useEffect(() => {
    if (selectedDogId) {
      let newPendingTests = [];
      if (testType === 'our-bbp') {
        newPendingTests = availableBbpDiseases.map(disease => ({
          testName: `BBP - ${disease}`,
          result: 'Pending',
          dateTested: null,
          isPending: true
        }));
      } else if (testType === 'our-body') {
        newPendingTests = availableBodyTests.map(category => ({
          testName: `Body Scores - ${category}`,
          result: 'Pending',
          dateTested: null,
          isPending: true
        }));
      } else if (testType === 'custom-disease' || testType === 'custom-body') {
        newPendingTests = customTests
          .filter(t => t.disease)
          .map(test => ({
            testName: testType === 'custom-body' ? `Body Scores - ${test.disease}` : test.disease,
            result: 'Pending',
            dateTested: null,
            isPending: true
          }));
      } else if (testType === 'write-in') {
        newPendingTests = customTests
          .filter(t => t.disease && t.result)
          .map(test => ({
            testName: test.disease,
            result: test.result,
            dateTested: null,
            isPending: true
          }));
      }
      setPendingTests(newPendingTests);
    }
  }, [testType, customTests, availableBbpDiseases, availableBodyTests, selectedDogId]);

  const addCustomTest = () => {
    setCustomTests([...customTests, { disease: '', result: '', writeInType: 'bbp' }]);
  };

  const removeCustomTest = (index) => {
    setCustomTests(customTests.filter((_, i) => i !== index));
  };

  const updateCustomTest = (index, field, value) => {
    const newTests = [...customTests];
    newTests[index][field] = value;
    setCustomTests(newTests);
  };

  const calculateCost = () => {
    if (testType === 'our-bbp') return 200;
    if (testType === 'our-body') return 800;
    if (testType === 'write-in') return 0;
    if (testType === 'custom-disease') {
      return customTests.reduce((total, test) => (test.disease ? total + 50 : total), 0);
    }
    if (testType === 'custom-body') {
      return customTests.reduce((total, test) => {
        if (!test.disease) return total;
        const costs = {
          Hips: 300,
          Elbows: 250,
          Patellas: 200,
          Thyroid: 150,
          Eyes: 150,
          Hearing: 150,
          Cardiac: 150
        };
        return total + (costs[test.disease] || 150);
      }, 0);
    }
    return 0;
  };

  const handleBuyTests = async () => {
    try {
      let url = `${API_BASE_URL}/buy-custom-health-test`;
      let payload = {
        playerId,
        dogId: selectedDogId,
        testType,
        tests: (testType === 'custom-disease' || testType === 'custom-body' || testType === 'write-in')
          ? customTests.filter(t => t.disease && (
              testType !== 'write-in' ? !selectedDogTests.some(r => 
                (r.testName || r.disease) === `Body Scores - ${t.disease}` || 
                (r.testName || r.disease) === t.disease
              ) : true
            )).map(t => ({
              disease: t.disease,
              result: t.result,
              writeInType: t.writeInType
            }))
          : [],
        externalLink: testType === 'write-in' ? externalLink : undefined
      };

      if (testType === 'our-bbp') {
        url = `${API_BASE_URL}/buy-bbp`;
        payload = { playerId, dogId: selectedDogId };
      } else if (testType === 'our-body') {
        url = `${API_BASE_URL}/buy-body-scores`;
        payload = { playerId, dogId: selectedDogId };
      }

      console.log('Sending buy tests payload:', payload, 'to', url);
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      console.log('Buy tests response:', data);
      if (!res.ok) throw new Error(data.error || data.message || 'Error buying tests');
      alert(`Tests completed! Cost: ${calculateCost()} DFG$`);
      setTestType('');
      setCustomTests([{ disease: '', result: '', writeInType: 'bbp' }]);
      setExternalLink('');
      setPendingTests([]);
      if (testType !== 'write-in') setSelectedDogId('');
      const dogsRes = await fetch(`${API_BASE_URL}/health-testing-dogs?playerId=${playerId}`)
        .then(res => res.json());
      setDogs(Array.isArray(dogsRes) ? dogsRes : []);
    } catch (error) {
      alert(error.message || 'Error buying tests');
      console.error('Buy tests error:', error);
    }
  };

  return (
    <div className="health-testing-container">
      <h2>Canine Health Testing</h2>
      <div className="form-section">
        <label>Select Dog:</label>
        <select
          value={selectedDogId}
          onChange={e => setSelectedDogId(e.target.value)}
          className="select-input"
        >
          <option value="">Select a dog</option>
          {dogs.map(dog => (
            <option
              key={dog.dogId}
              value={dog.dogId}
              disabled={!(canBuyTests[dog.dogId]?.canBuyBBP || canBuyTests[dog.dogId]?.canBuyBody || canBuyTests[dog.dogId]?.canBuyWriteIn)}
            >
              {dog.registeredName}
              {!(canBuyTests[dog.dogId]?.canBuyBBP || canBuyTests[dog.dogId]?.canBuyBody || canBuyTests[dog.dogId]?.canBuyWriteIn) && ' (No tests available)'}
            </option>
          ))}
        </select>
      </div>
      {selectedDogId && (
        <div className="dog-info-section">
          <h3>{dogs.find(d => d.dogId === selectedDogId)?.registeredName}</h3>
          <div className="thumbnail-container">
            {selectedDogArtUrl ? (
              <img
                src={selectedDogArtUrl}
                alt={`${dogs.find(d => d.dogId === selectedDogId)?.registeredName}'s thumbnail`}
                className="dog-thumbnail"
                onError={() => console.error(`Failed to load image for ${selectedDogId}: ${selectedDogArtUrl}`)}
              />
            ) : (
              <p>No image available for this dog.</p>
            )}
          </div>
          {(selectedDogTests.length > 0 || pendingTests.length > 0) ? (
            <div className="health-tests-section">
              <h4>Health Test Records</h4>
              <table className="health-tests-table">
                <thead>
                  <tr>
                    <th>Test Name</th>
                    <th>Result</th>
                    <th>Date Tested</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDogTests.map((test, index) => (
                    <tr key={`completed-${index}`}>
                      <td>{test.testName || test.disease}</td>
                      <td>{test.result}</td>
                      <td>{test.dateTested ? new Date(test.dateTested).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                  {pendingTests.map((test, index) => (
                    <tr key={`pending-${index}`} style={{ color: 'blue' }}>
                      <td>{test.testName}</td>
                      <td>{test.result}</td>
                      <td>-</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p>No health test records available.</p>
          )}
        </div>
      )}
      <div className="form-section">
        <label>Test Type:</label>
        <select
          value={testType}
          onChange={e => setTestType(e.target.value)}
          className="select-input"
        >
          <option value="">Select Test Type</option>
          <option value="our-bbp" disabled={!canBuyTests[selectedDogId]?.canBuyBBP}>
            DFG Breed Specific Blood Panel (200 DFG$)
            {!canBuyTests[selectedDogId]?.canBuyBBP && ' - All tests completed'}
          </option>
          <option value="our-body" disabled={!canBuyTests[selectedDogId]?.canBuyBody}>
            Orthopedic Evaluation and Health Screening (800 DFG$)
            {!canBuyTests[selectedDogId]?.canBuyBody && ' - All tests completed'}
          </option>
          <option value="custom-disease" disabled={bbpDiseases.length === 0}>
            Custom Disease Panel (50 DFG$ per disease)
            {bbpDiseases.length === 0 && ' - All tests completed'}
          </option>
          <option value="custom-body" disabled={availableBodyTests.length === 0}>
            Custom Orthopedic/Health Screening (Per Test Cost)
            {availableBodyTests.length === 0 && ' - All tests completed'}
          </option>
          <option value="write-in" disabled={!canBuyTests[selectedDogId]?.canBuyWriteIn}>
            Submit External Health Test Record (Free with Link)
          </option>
        </select>
      </div>
      {(testType === 'custom-disease' || testType === 'custom-body' || testType === 'write-in') && (
        <div className="custom-tests-section">
          <h3>
            {testType === 'custom-disease' ? 'Custom Disease Selection' :
             testType === 'custom-body' ? 'Custom Orthopedic/Health Screening' :
             'Write-In Health Test'}
          </h3>
          {customTests.map((test, index) => (
            <div key={index} className="test-row">
              {testType === 'custom-disease' ? (
                <select
                  value={test.disease}
                  onChange={e => updateCustomTest(index, 'disease', e.target.value)}
                  className="select-input"
                >
                  <option value="">Select Disease</option>
                  {bbpDiseases.map(d => (
                    <option key={d} value={d} disabled={testedDiseases.includes(d)}>
                      {d} {testedDiseases.includes(d) ? '(Tested)' : ''}
                    </option>
                  ))}
                </select>
              ) : testType === 'custom-body' ? (
                <select
                  value={test.disease}
                  onChange={e => updateCustomTest(index, 'disease', e.target.value)}
                  className="select-input"
                >
                  <option value="">Select Test</option>
                  {availableBodyTests.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Test Name (e.g., Hips, Thyroid, Custom Disease)"
                    value={test.disease}
                    onChange={e => updateCustomTest(index, 'disease', e.target.value)}
                    className="text-input"
                  />
                  <input
                    type="text"
                    placeholder="Result (e.g., Clear, Affected, Excellent)"
                    value={test.result}
                    onChange={e => updateCustomTest(index, 'result', e.target.value)}
                    className="text-input"
                  />
                  <select
                    value={test.writeInType}
                    onChange={e => updateCustomTest(index, 'writeInType', e.target.value)}
                    className="select-input"
                  >
                    <option value="bbp">BBP Disease</option>
                    <option value="body">Body Score</option>
                  </select>
                </>
              )}
              {(testType === 'custom-disease' || testType === 'custom-body' || testType === 'write-in') && (
                <button
                  onClick={() => removeCustomTest(index)}
                  className="action-button remove-button"
                  style={{ backgroundColor: '#e57373', marginLeft: '10px' }}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
          {testType === 'write-in' && (
            <input
              type="text"
              placeholder="External Link (required)"
              value={externalLink}
              onChange={e => setExternalLink(e.target.value)}
              className="text-input"
            />
          )}
          <button onClick={addCustomTest} className="action-button">Add Another Test</button>
        </div>
      )}
      <div className="summary-section">
        <p>Total Cost: {calculateCost()} DFG$</p>
        <button
          onClick={handleBuyTests}
          disabled={
            !selectedDogId ||
            !testType ||
            ((testType === 'custom-disease' || testType === 'custom-body') && customTests.every(t => !t.disease)) ||
            (testType === 'write-in' && (customTests.every(t => !t.disease || !t.result || !t.writeInType) || !externalLink))
          }
          className="action-button"
        >
          Buy Tests
        </button>
      </div>
    </div>
  );
};

export default CanineHealthTesting;